import '/src/styles/main.scss';

import 'lazysizes';
import 'lazysizes/plugins/bgset/ls.bgset.js';
import 'lazysizes/plugins/parent-fit/ls.parent-fit';
import 'lazysizes/plugins/respimg/ls.respimg';
import ScrollAnimations from './modules/scrollAnimations';
import ToggleInView from './modules/toggleInView';
import Splide from '@splidejs/splide';
import Swup from 'swup';
import SwupScriptsPlugin from '@swup/scripts-plugin';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupRouteNamePlugin from '@swup/route-name-plugin';
import SwupBodyClassPlugin from '@swup/body-class-plugin';

// Conditionally load object fit polyfill if needed
if (!('object-fit' in document.createElement('a').style)) {
  require('lazysizes/plugins/object-fit/ls.object-fit');
}

// Add easeOutExpo easing function
function easeOutExpo(x, t, b, c, d) {
  return t === d ? b + c : c * (-Math.pow(2, (-10 * t) / d) + 1) + b;
}

//
// Smooth Anchor Links
//
document.querySelectorAll('a[href*="#"]:not([href="#"])').forEach(anchor => {
  anchor.addEventListener('click', function (e) {
    e.preventDefault();
    if (
      location.pathname.replace(/^\//, '') === this.pathname.replace(/^\//, '') &&
      location.hostname === this.hostname
    ) {
      const target = document.querySelector(this.hash) || document.querySelector(`[name=${this.hash.slice(1)}]`);
      if (target) {
        const start = window.scrollY;
        const end = target.getBoundingClientRect().top + window.scrollY;
        const duration = 750;
        let startTime = null;

        function animateScroll(currentTime) {
          if (!startTime) startTime = currentTime;
          const timeElapsed = currentTime - startTime;
          const run = easeOutExpo(0, timeElapsed, start, end - start, duration);
          window.scrollTo(0, run);
          if (timeElapsed < duration) requestAnimationFrame(animateScroll);
        }

        requestAnimationFrame(animateScroll);
      }
    }
  });
});

//
// Global Resized Event
//
let resizeTimeout;
const resizedEvent = new Event('resized');

window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    window.dispatchEvent(resizedEvent);
  }, 250); // Adjust the timeout duration as needed
});

//
// Global State Classes
//
const isVisibleClass = '--is-visible';
const isOpenClass = '--is-open';
const isToggledClass = '--is-toggled';
const isActiveClass = '--is-active';
const isLoadingClass = '--is-loading';
const isHiddenClass = '--is-hidden';
const isSelectedClass = '--is-selected';
const isAnimatingClass = '--is-animating';

//
// Nav
//
function initNav() {
  const nav = document.querySelector('.js-site-nav');
  if (!nav) return;

  const navOpens = document.querySelectorAll('.js-open-nav');
  const navCloses = document.querySelectorAll('.js-close-nav');

  navOpens.forEach(btn => {
    if (btn.dataset.navInit) return;
    btn.addEventListener('click', () => {
      nav.classList.toggle(isVisibleClass);
    });
    btn.dataset.navInit = 'true';
  });

  navCloses.forEach(btn => {
    if (btn.dataset.navInit) return;
    btn.addEventListener('click', () => {
      nav.classList.remove(isVisibleClass);
    });
    btn.dataset.navInit = 'true';
  });
}

const siteHeader = document.querySelector('.js-site-header');

//
// Init Scripts
//
function init() {

  initNav();

  //
  // Scroll Animations
  //
  ScrollAnimations();

  //
  // Toggle In View
  //
  ToggleInView();

  //
  // Hero Galleries
  //
  const heroSliderEl = document.querySelector('.js-hero-slider');

  if (heroSliderEl) {
    const heroSlider = new Splide(heroSliderEl, {
      autoWidth: false,
      pagination: false,
      autoHeight: false,
      perPage: 1,
      perMove: 1,
      gap: '0',
      type: 'fade',
      rewind: true,
      updateOnMove: false,
      cloneStatus: false,
      autoplay: true,
      interval: 4000,
      speed: 2500,
      arrows: false,
      pauseOnHover: false,
    });

    heroSlider.mount();

    // On mounted add is-animating to the first slide
    const firstSlide = heroSliderEl.querySelector('.hero__carousel-slide.splide__slide');
    if (firstSlide) {
      firstSlide.classList.add('is-animating');
    } 

    // On each slide change, add is-visible to the current slide and remove from others
    heroSlider.on('move', (newIndex) => {
      const slides = heroSliderEl.querySelectorAll('.splide__slide');
      slides.forEach((slide, index) => {
        if (index === newIndex) {
          slide.classList.add('is-animating');
        } else {
          setTimeout(() => {
            slide.classList.remove('is-animating');
          }, 2500);
        }
      });
    });
  }


  // 
  // Hero Jump to Content
  //
  const jumpBtn = document.querySelector('.js-scroll-to-content');
  const pageContent = document.querySelector('.js-page-content');
  if (jumpBtn && pageContent) {
    jumpBtn.addEventListener('click', () => {
      const contentTop = pageContent.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: contentTop,
        behavior: 'smooth'
      });
    });
  }

  //
  // Splash Buttons and Images
  //
  const splashButtons = document.querySelectorAll('.js-splash-btn');
  const splashImages = document.querySelectorAll('.js-splash-img');

  splashButtons.forEach((button, index) => {
    // When entered, add '--is-hovered' to corresponding image
    button.addEventListener('mouseenter', () => {
      splashImages[index].classList.add('--is-hovered');
      splashButtons[index].classList.add('--is-hovered');
    });

    // When exited, remove '--is-hovered' from corresponding image
    button.addEventListener('mouseleave', () => {
      splashImages[index].classList.remove('--is-hovered');
      splashButtons[index].classList.remove('--is-hovered');
    });
  });

  // Do the same for hovering and exiting the images
  splashImages.forEach((image, index) => {
    image.addEventListener('mouseenter', () => {
      splashButtons[index].classList.add('--is-hovered');
      splashImages[index].classList.add('--is-hovered');
    });

    image.addEventListener('mouseleave', () => {
      splashButtons[index].classList.remove('--is-hovered');
      splashImages[index].classList.remove('--is-hovered');
    });
  });

  //
  // Logo Galleries
  //
  const logoSliders = document.getElementsByClassName('js-logo-slider');

  for (let i = 0; i < logoSliders.length; i++) {
    const logoSlider = new Splide(logoSliders[i], {
      autoWidth: false,
      pagination: false,
      autoHeight: true,
      perPage: 1,
      perMove: 1,
      gap: '0',
      type: 'fade',
      rewind: true,
      updateOnMove: true,
      cloneStatus: false,
      speed: 1000,
      autoplay: true,
      interval: 3000,
      pauseOnHover: false,
      arrows: false,
    });

    logoSlider.mount();
  }

  //
  // News Galleries
  //
  const newsSliders = document.getElementsByClassName('js-news-slider');

  for (let i = 0; i < newsSliders.length; i++) {
    const newsSlider = new Splide(newsSliders[i], {
      autoWidth: true,
      pagination: false,
      autoHeight: true,
      perPage: 1,
      perMove: 1,
      gap: '20px',
      type: 'slide',
      rewind: true,
      updateOnMove: true,
      cloneStatus: false,
      speed: 1000,
      autoplay: false,
      arrows: false,
    });

    const prevSlideBtn = newsSliders[i].parentElement.querySelector('.js-news-slider-prev');
    const nextSlideBtn = newsSliders[i].parentElement.querySelector('.js-news-slider-next');

    if (prevSlideBtn) {
      prevSlideBtn.addEventListener('click', () => {
        newsSlider.go('<');
      });
    }

    if (nextSlideBtn) {
      nextSlideBtn.addEventListener('click', () => {
        newsSlider.go('>');
      });
    }
    newsSlider.mount();
  }



  //
  // News Infinite Scroll
  //
  const newsContainer = document.querySelector('.js-news-posts');
  let moreNewsIndicator = document.querySelector('.js-more-news');
  var moreNewsUrl = null;

  if (newsContainer && moreNewsIndicator) {

    // When the moreNewsIndicator comes into view, add an isLoadingClass and fetch more posts
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {

            // Add loading class
            moreNewsIndicator.classList.add(isLoadingClass);

            // Fetch more posts from the entry
            moreNewsUrl = entry.target.dataset.nextPage;

            fetch(moreNewsUrl)
              .then(response => response.text())
              .then(data => {

                // Parse the returned HTML string
                const parser = new DOMParser();
                const doc = parser.parseFromString(data, 'text/html');

                // Extract the inner content of .js-news-posts of new page
                const newNewsContainer = doc.querySelector('.js-news-posts');
                const newPosts = newNewsContainer ? Array.from(newNewsContainer.children) : [];

                // Update indicator classes
                moreNewsIndicator.classList.remove(isLoadingClass);
                moreNewsIndicator.classList.add(isHiddenClass);

                // Remove the old moreNewsIndicator
                moreNewsIndicator.remove();

                // Append new posts to the newsContainer
                newPosts.forEach(post => {
                  newsContainer.appendChild(post);
                  setTimeout(() => {
                    post.classList.remove('--is-hidden');
                  }, 100);
                });

                // If there's a new moreNewsIndicator, update the observer
                moreNewsIndicator = newsContainer.querySelector('.js-more-news');
                if (moreNewsIndicator) {
                  observer.unobserve(moreNewsIndicator);
                  observer.observe(moreNewsIndicator);
                }
              });
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 1.0
      }
    );

    observer.observe(moreNewsIndicator);
  }


  // 
  // Partner Filtering
  //
  const filters = document.querySelectorAll('.js-filter');
  const partners = document.querySelectorAll('.js-partner');

  filters.forEach(filter => {
    filter.addEventListener('click', () => {
      const selectedFilter = filter.dataset.filter;

      // Update active state on filters
      filters.forEach(f => f.classList.remove(isActiveClass));
      filter.classList.add(isActiveClass);

      // Show/hide partners based on selected filter
      partners.forEach(partner => {
        const partnerCategories = partner.dataset.category;

        if (selectedFilter === 'all' || partnerCategories.includes(selectedFilter)) {
          partner.classList.remove(isHiddenClass);
        } else {
          partner.classList.add(isHiddenClass);
        }
      });
    });
  });
}

//
// Swup
//
const swup = new Swup({
  plugins: [
    new SwupScriptsPlugin({
      head: true,
      body: false
    }),
    new SwupHeadPlugin({
      awaitAssets: true,
      persistAssets: true,
    }),
    new SwupRouteNamePlugin({
      routes: [
        { name: 'home', path: '/' },
        { name: 'contact', path: '/contact' },
        { name: 'news', path: '/news(.*)' },
        { name: 'team', path: '/team(.*)' },
      ],
    }),
    new SwupBodyClassPlugin(),
  ],
});

// Run once when page loads
init();

// Run when navigating to new page
swup.hooks.on('animation:out:start', () => {
  siteHeader.classList.add('--is-transitioning');
});

// Run after every additional navigation by swup
swup.hooks.on('page:view', () => {
  init();


  // Google Tag Manager Virtual Page View
  // if (window.dataLayer) {
  //   window.dataLayer.push({
  //     event: 'VirtualPageview',
  //     virtualPageURL: window.location.pathname + window.location.search,
  //     virtualPageTitle: document.title,
  //   });
  // }
});

// Visit ended: all content is replaced, animations have finished.
swup.hooks.on('animation:in:end', visit => {
  // Add any additional logic here if needed
  if (siteHeader) {
    setTimeout(() => {
      siteHeader.classList.remove('--is-transitioning');
    }, 100);
  }
});